import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Check, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TextareaControl, TextControl } from '@/frontend/features/Fde/FormControls'
import ProjectIdentityFields from '@/frontend/features/Fde/ProjectIdentityFields'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { FdeCopy, Project } from '@/frontend/features/Fde/types'

interface Props {
  project: Project
  copy: FdeCopy
}

type WorkspaceSettingsData = {
  task_prefix: string
  name: string
  description: string
  icon: string
  logo: File | null
  remove_logo: boolean
  objective: string
  repository_remote_url: string
  repository_branch: string
}

const projectValues = (project: Project): WorkspaceSettingsData => ({
  task_prefix: project.task_prefix,
  name: project.name,
  description: project.description ?? '',
  icon: project.icon,
  logo: null,
  remove_logo: false,
  objective: project.objective ?? '',
  repository_remote_url: project.repository_remote_url ?? '',
  repository_branch: project.repository_branch,
})

export default function ProjectWorkspaceSettingsForm({ project, copy }: Props) {
  const form = useForm<WorkspaceSettingsData>(projectValues(project))

  useEffect(() => {
    const values = projectValues(project)
    form.setDefaults(values)
    form.setData(values)
    form.clearErrors()
  }, [project.id, project.updated_at])

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.transform((data) => ({ project: data }))
    form.patch(fdeProjectPath(`${fdeRoutes.projects}/${project.id}`, project.id, { return_to: 'settings' }), {
      preserveScroll: true,
      forceFormData: Boolean(form.data.logo || form.data.remove_logo),
    })
  }

  const firstError = Object.values(form.errors)[0]

  return (
    <form onSubmit={submit} className="pb-8">
      <header className="flex flex-col gap-4 border-b border-border px-5 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-semibold">{copy.management.settings_workspace}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {copy.management.settings_workspace_description}
          </p>
        </div>
        <div className="flex items-center gap-3 self-end sm:self-auto">
          {form.recentlySuccessful && (
            <span className="flex items-center gap-1.5 text-xs text-emerald-700">
              <Check className="size-3.5" />
              {copy.management.workspace_saved}
            </span>
          )}
          <Button
            type="submit"
            size="sm"
            disabled={form.processing || !form.isDirty || !form.data.name.trim()}
          >
            <Save />
            {copy.common.save}
          </Button>
        </div>
      </header>

      {firstError && (
        <p
          role="alert"
          className="mx-5 mt-5 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
        >
          {firstError}
        </p>
      )}

      <div className="mx-auto grid max-w-5xl gap-7 px-5 py-6">
        <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="grid content-start gap-5">
            <TextControl
              label={copy.forms.name}
              value={form.data.name}
              onChange={(event) => form.setData('name', event.target.value)}
              required
            />
            <div>
              <TextControl
                label={copy.management.task_prefix}
                value={form.data.task_prefix}
                onChange={(event) => form.setData('task_prefix', event.target.value.toUpperCase())}
                disabled={project.task_prefix_locked}
                maxLength={8}
              />
              <p className="mt-1.5 text-xs text-muted-foreground">
                {project.task_prefix_locked
                  ? copy.management.task_prefix_locked
                  : copy.management.task_prefix_hint}
              </p>
            </div>
            <TextareaControl
              label={copy.forms.description}
              value={form.data.description}
              onChange={(event) => form.setData('description', event.target.value)}
              rows={3}
            />
          </div>
          <ProjectIdentityFields
            name={form.data.name}
            icon={form.data.icon}
            logoUrl={project.logo_url}
            logo={form.data.logo}
            removed={form.data.remove_logo}
            copy={copy}
            onIcon={(icon) => form.setData('icon', icon)}
            onLogo={(logo, removeLogo) =>
              form.setData((data) => ({ ...data, logo, remove_logo: removeLogo }))
            }
          />
        </section>

        <TextareaControl
          label={copy.forms.objective}
          value={form.data.objective}
          onChange={(event) => form.setData('objective', event.target.value)}
          rows={4}
        />

        <section className="grid gap-5 border-t border-border pt-7 sm:grid-cols-[minmax(0,1fr)_12rem]">
          <div>
            <TextControl
              label={copy.forms.repository_remote_url}
              value={form.data.repository_remote_url}
              onChange={(event) => form.setData('repository_remote_url', event.target.value)}
              placeholder={copy.forms.repository_remote_example}
            />
            <p className="mt-1.5 text-xs text-muted-foreground">{copy.forms.repository_remote_url_hint}</p>
          </div>
          <TextControl
            label={copy.forms.repository_branch}
            value={form.data.repository_branch}
            onChange={(event) => form.setData('repository_branch', event.target.value)}
            required
          />
        </section>

      </div>
    </form>
  )
}
